import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "@/hooks/use-toast";
import { useLogger } from "@/lib/logger";
import * as Sentry from "@sentry/react";
import { trpc } from "@/lib/trpc";
import { useEffect } from "react";

export const useStockAge = (enabled: boolean = true) => {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const logger = useLogger();

  const { data, isLoading, error, refetch } = trpc.reporting.getStockAge.useQuery(
    {
      // S0-3: organizationId is derived server-side from auth; not sent here.
      // `organization?.id` still gates the query via `enabled` below.
      locationId: selectedLocation?.id ?? "00000000-0000-0000-0000-000000000000",
    },
    {
      enabled: enabled && !!selectedLocation?.id && !!organization?.id,
      staleTime: 60 * 60 * 1000,
      gcTime: 2 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  // Handle errors with toast and Sentry
  useEffect(() => {
    if (!error) return;
    const err = error instanceof Error ? error : new Error('Unknown error');

    Sentry.captureException(error, {
      tags: {
        feature: 'reporting',
        operation: 'stock_age',
        location_id: selectedLocation?.id,
      },
    });

    logger.error('Stock age fetch failed', {
      operation: 'fetch_stock_age',
      error: err.message,
      locationId: selectedLocation?.id,
      locationName: selectedLocation?.name,
    });

    toast({
      title: 'Error',
      description: 'Failed to fetch stock age data',
      variant: 'destructive',
    });
  }, [error, logger, selectedLocation?.id, selectedLocation?.name]);

  return {
    stockAgeData: data || [],
    loading: isLoading,
    error,
    refetch
  };
};
